import { z } from "zod";

/**
 * LINE API RICH MENU DEFINITIONS
 */
export const richMenuSizeSchema = z.object({
	width: z.number().min(800).max(2500),
	height: z.number().min(250),
});

export const richMenuBoundsSchema = z.object({
	x: z.number(),
	y: z.number(),
	width: z.number().min(0),
	height: z.number().min(0),
});

export const richmenuActionMessageSchema = z.object({
	type: z.literal("message"),
	label: z.string().optional(),
	text: z.string().max(300),
});

export const richmenuActionURISchema = z.object({
	type: z.literal("uri"),
	label: z.string().optional(),
	uri: z.string().max(1000),
});

export const richmenuActionSwitchSchema = z.object({
	type: z.literal("richmenuswitch"),
	label: z.string().max(20).optional(),
	richMenuAliasId: z.string(),
	data: z.string().max(300),
});

export const richMenuActionSchema = z.union([
	richmenuActionMessageSchema,
	richmenuActionURISchema,
	richmenuActionSwitchSchema,
]);

export const richMenuAreaSchema = z.object({
	bounds: richMenuBoundsSchema,
	action: richMenuActionSchema,
});

export const richMenuTabGroupStatusSchema = z.enum([
	"DRAFT",
	"PUBLISHED",
] as const);

export const richMenuDisplayPrioritySchema = z.enum([
	"USER",
	"DEFAULT",
] as const);

export const richMenuTemplateNameSchema = z.enum([
	"RICH_MENU_TEMPLATE_101",
	"RICH_MENU_TEMPLATE_102",
	"RICH_MENU_TEMPLATE_103",
	"RICH_MENU_TEMPLATE_104",
	"RICH_MENU_TEMPLATE_105",
	"RICH_MENU_TEMPLATE_201",
	"RICH_MENU_TEMPLATE_202",
	"RICH_MENU_TEMPLATE_203",
	"RICH_MENU_TEMPLATE_204",
	"RICH_MENU_TEMPLATE_205",
	"RICH_MENU_TEMPLATE_206",
	"RICH_MENU_TEMPLATE_207",
	"RICH_MENU_CUSTOM_TEMPLATE_201",
	"RICH_MENU_CUSTOM_TEMPLATE_202",
] as const);

export const richMenuTemplateSchema = z.object({
	name: richMenuTemplateNameSchema,
	size: richMenuSizeSchema,
	areas: z.array(richMenuAreaSchema),
});

export const richMenuSchema = z.object({
	id: z.number().describe("auto generated by DB"),
	groupId: z.number().describe("for grouping purposes"),
	name: z.string().max(300).nullable().describe("for managers"),
	richMenuId: z.string().max(64).nullable().describe("generated by LINE API"),
	richMenuAliasId: z
		.string()
		.max(32)
		.nullable()
		.describe("used for switch actions"),
	imageName: z.string().nullable().describe("rich menu image name on server"),
	areas: richMenuAreaSchema
		.array()
		.min(0)
		.max(20)
		.describe("clickable areas of the rich menu. max 20"),
	templateType: z.preprocess(
		(val) => (val === "" ? null : val),
		richMenuTemplateNameSchema.nullable(),
	),
	size: richMenuSizeSchema,
	chatBarText: z.string().max(14),
	tabIndex: z
		.number()
		.min(0)
		.max(2)
		.nullable()
		.describe("index from left to right, 0 is leftmost"),
	selected: z
		.boolean()
		.describe("richmenu is shown by default, when user opens chat"),
});

export const richMenuPartialSchema = richMenuSchema.partial();

export const richMenuAliasSchema = z.object({
	richMenuId: z.string(),
	richMenuAliasId: z.string(),
});

export const richMenuTabGroupSchema = z.object({
	groupId: z.number(),
	name: z.string().max(100),
	displayPriority: richMenuDisplayPrioritySchema,
	status: richMenuTabGroupStatusSchema,
	createdAt: z.preprocess(
		(val) => (typeof val === "string" ? new Date(val) : val),
		z.date().nullable(),
	),
	updatedAt: z.preprocess(
		(val) => (typeof val === "string" ? new Date(val) : val),
		z.date().nullable(),
	),
});

export const richMenuPrePublishSchema = z.object({
	id: z.number(),
	groupId: z.number(),
	richMenuId: z.null(),
	richMenuAliasId: z.null(),
	name: z.string().max(300),
	chatBarText: z.string().max(14),
	selected: z.boolean(),
	size: richMenuSizeSchema,
	areas: richMenuAreaSchema.array().min(0).max(20),
	imageName: z.string(),
	templateType: z.preprocess(
		(val) => (val === "" ? null : val),
		richMenuTemplateNameSchema.nullable(),
	),
	tabIndex: z.number().min(0).max(2),
});

export const richMenuTabGroupPrePublishSchema = z.object({
	groupId: richMenuTabGroupSchema.shape.groupId,
	name: richMenuTabGroupSchema.shape.name,
	status: z.literal(richMenuTabGroupStatusSchema.enum.DRAFT),
	richmenus: richMenuPrePublishSchema.array().min(1).max(3),
});

export const richMenuPostPublishSchema = z.object({
	groupId: z.number(),
	richMenuId: z.string().max(64),
	richMenuAliasId: z.string().max(32),
	name: z.string().max(300),
	chatBarText: z.string().max(14),
	selected: z.boolean(),
	size: richMenuSizeSchema,
	areas: richMenuAreaSchema.array().min(0).max(20),
	imageName: z.string(),
	templateType: z.preprocess(
		(val) => (val === "" ? null : val),
		richMenuTemplateNameSchema.nullable(),
	),
	tabIndex: z.number().min(0).max(2),
});

export const richMenuTabGroupPostPublishSchema = z.object({
	groupId: richMenuTabGroupSchema.shape.groupId,
	name: richMenuTabGroupSchema.shape.name,
	status: z.literal(richMenuTabGroupStatusSchema.enum.PUBLISHED),
	richmenus: richMenuPostPublishSchema.array().min(1).max(3),
});

export const richMenuTabGroupPostUnpublishSchema = z.object({
	groupId: richMenuTabGroupSchema.shape.groupId,
	name: richMenuTabGroupSchema.shape.name,
	status: z.literal(richMenuTabGroupStatusSchema.enum.DRAFT),
	richmenus: richMenuPrePublishSchema.array().min(1).max(3),
});
